import React, { forwardRef, useImperativeHandle, useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import DeleteIcon from '../../static/image/deleteIcon.svg';
import { setValueCapabilities } from '../../redux/productSlice';
import CustomInput from '../../components/customInput/CustomInput';
import ModalFileUpload from '../../components/ModalFileUpload/ModalFileUpload';
import CommonDeleteModal from '../../components/commonDeleteModal/CommonDeleteModal';
import { uploadAsset, deleteAsset } from '../../redux/assetSlice';
import { ASSET_LOCATION_MAPPING, ASSET_TYPE_MAPPING, LABEL_DELETE_ASSET } from '../../utils/constant';
import { isEmpty } from '../../utils/commonUtil';

const Capabilities = forwardRef(({ language, productId, formType, onShowAlert }, ref) => {
  const dispatch = useDispatch();
  const productState = useSelector((state) => state.product);
  const product = productState.data || {};

  const capabilitiesData = product.capabilities || product.contentData?.capabilities || {};
  const capabilitiesItems = capabilitiesData.items || [];
  const capabilitiesTitle = capabilitiesData.title || '';
  const capabilitiesDescription = capabilitiesData.description || '';

  const [showFileUploadModal, setShowFileUploadModal] = useState(false);
  const [isShowDeleteAsset, setIsShowDeleteAsset] = useState(null);
  const [selectedCapabilityIndex, setSelectedCapabilityIndex] = useState(null);
  const [selectedField, setSelectedField] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [showDeleteGroupModal, setShowDeleteGroupModal] = useState(false);
  const [groupToDeleteIndex, setGroupToDeleteIndex] = useState(null);

  const PLACEHOLDER_CONFIG = {
    title: {
      EN: "Enter capabilities title EN",
      CN: "Enter capabilities title CN"
    },
    description: {
      EN: "Enter capabilities description EN",
      CN: "Enter capabilities description CN"
    },
    capabilityTitle: {
      EN: "Enter capability title EN",
      CN: "Enter capability title CN"
    },
    capabilityDescription: {
      EN: "Enter capability description EN",
      CN: "Enter capability description CN"
    },
    mediaUrl: {
      EN: "Enter media URL EN",
      CN: "Enter media URL CN"
    },
    retainCapability: {
      EN: "At least retain one capability item EN",
      CN: "At least retain one capability item CN"
    }
  };

  const MEDIA_TYPES = [
    { value: 'image', label: 'Image' },
    { value: 'video', label: 'Video' }
  ];

  const DEFAULT_MEDIA_TYPE = 'video';

  const INITIAL_CAPABILITY_ITEM = {
    title: '',
    description: '',
    media: {
      mediaType: DEFAULT_MEDIA_TYPE,
      mediaUrl: '',
      mediaFileId: ''
    }
  };

  useEffect(() => {
    if (!capabilitiesData.title && capabilitiesItems.length === 0) {
      dispatch(setValueCapabilities({
        value: {
          title: '',
          description: '',
          items: [INITIAL_CAPABILITY_ITEM]
        }
      }));
    } else {
      const updatedItems = capabilitiesItems.map(item => ({
        ...item,
        media: {
          mediaType: item.media?.mediaType || DEFAULT_MEDIA_TYPE,
          mediaUrl: item.media?.mediaUrl || '',
          mediaFileId: item.media?.mediaFileId || ''
        }
      }));
      if (capabilitiesItems.some((item, index) =>
        item.media?.mediaType !== updatedItems[index].media.mediaType
      )) {
        dispatch(setValueCapabilities({
          value: {
            ...capabilitiesData,
            items: updatedItems
          }
        }));
      }
    }
  }, [capabilitiesData, capabilitiesItems.length, dispatch]);

  const handleFileUpload = (file) => {
    if (!file) return;

    const mediaType = capabilitiesItems[selectedCapabilityIndex]?.media?.mediaType || DEFAULT_MEDIA_TYPE;

    if (mediaType === 'image' &&
      !file.type.includes('png') &&
      !file.type.includes('jpeg') &&
      !file.type.includes('jpg') &&
      !file.name.toLowerCase().match(/\.(png|jpe?g)$/)) {
      onShowAlert("Please upload PNG, JPEG or JPG format for image files", false);
      return;
    }

    setIsUploading(true);
    const formData = new FormData();
    formData.append("uuid", formType === 'EDIT' ? productId : "");
    formData.append("assetLocation", ASSET_LOCATION_MAPPING[selectedField]);
    formData.append("assetType", ASSET_TYPE_MAPPING.product);
    formData.append("file", file);

    dispatch(uploadAsset(formData))
      .then((res) => {
        if (res?.payload) {
          const fileId = res.payload.resdata.fileId;
          const fileUrl = res.payload.resdata.url;
          dispatch((dispatch, getState) => {
            const state = getState();
            const product = state.product.data || {};
            const capabilitiesData = product.capabilities || product.contentData?.capabilities || {};
            const currentCapabilitiesItems = capabilitiesData.items || [];

            const newItems = currentCapabilitiesItems.map((item, i) => {
              if (i === selectedCapabilityIndex) {
                return {
                  ...item,
                  media: {
                    ...item.media,
                    mediaUrl: fileUrl,
                    mediaFileId: fileId
                  }
                };
              }
              return item;
            });

            dispatch(setValueCapabilities({
              value: {
                ...capabilitiesData,
                items: newItems
              }
            }));
          });
        }
        if (res?.error) {
          onShowAlert(res.error.message, false);
        }
      })
      .catch((err) => {
        onShowAlert(err.message || "Upload failed", false);
      })
      .finally(() => {
        setIsUploading(false);
        setShowFileUploadModal(false);
        setSelectedCapabilityIndex(null);
        setSelectedField(null);
      });
  };

  const handleDeleteFile = () => {
    const fileId = capabilitiesItems[selectedCapabilityIndex]?.media?.mediaFileId;
    const newItems = capabilitiesItems.map((item, i) => {
      if (i === selectedCapabilityIndex) {
        return {
          ...item,
          media: {
            ...item.media,
            mediaUrl: "",
            mediaFileId: ""
          }
        };
      }
      return item;
    });

    dispatch(setValueCapabilities({
      value: {
        ...capabilitiesData,
        items: newItems
      }
    }));

    if (fileId) {
      dispatch(deleteAsset(fileId))
        .then((res) => {
          if (res?.error &&
            !res.error.message.includes("No active document") &&
            !res.error.message.includes("Unexpected token")) {
            onShowAlert(res.error.message, false);
          }
        })
        .catch((err) => {
          if (err.message &&
            !err.message.includes("No active document") &&
            !err.message.includes("Unexpected token")) {
            onShowAlert(err.message, false);
          }
        })
        .finally(() => {
          setIsShowDeleteAsset(null);
          setSelectedCapabilityIndex(null);
          setSelectedField(null);
        });
    } else {
      setIsShowDeleteAsset(null);
      setSelectedCapabilityIndex(null);
      setSelectedField(null);
    }
  };

  const handleShowUploadFile = (capabilityIndex, field) => {
    setSelectedCapabilityIndex(capabilityIndex);
    setSelectedField(field);
    setShowFileUploadModal(true);
  };

  const handleShowDeleteFile = (capabilityIndex, field) => {
    setSelectedCapabilityIndex(capabilityIndex);
    setSelectedField(field);
    setIsShowDeleteAsset(LABEL_DELETE_ASSET);
  };

  const handleCancelUploadFile = () => {
    setShowFileUploadModal(false);
    setSelectedCapabilityIndex(null);
    setSelectedField(null);
  };

  const addGroup = () => {
    dispatch(setValueCapabilities({
      value: {
        ...capabilitiesData,
        items: [...capabilitiesItems, INITIAL_CAPABILITY_ITEM]
      }
    }));
  };

  const showDeleteGroupConfirmation = (index) => {
    if (capabilitiesItems.length <= 1) {
      alert(PLACEHOLDER_CONFIG.retainCapability[language]);
      return;
    }
    setGroupToDeleteIndex(index);
    setShowDeleteGroupModal(true);
  };

  const confirmDeleteGroup = () => {
    const newItems = capabilitiesItems.filter((_, i) => i !== groupToDeleteIndex);
    dispatch(setValueCapabilities({
      value: {
        ...capabilitiesData,
        items: newItems
      }
    }));
    setShowDeleteGroupModal(false);
    setGroupToDeleteIndex(null);
  };

  const cancelDeleteGroup = () => {
    setShowDeleteGroupModal(false);
    setGroupToDeleteIndex(null);
  };

  const updateCapabilities = (field, value) => {
    dispatch(setValueCapabilities({
      value: { ...capabilitiesData, [field]: value }
    }));
  };

  const updateCapabilityItem = (index, field, value) => {
    const newItems = capabilitiesItems.map((item, i) =>
      i === index ? { ...item, [field]: value } : item
    );
    dispatch(setValueCapabilities({
      value: {
        ...capabilitiesData,
        items: newItems
      }
    }));
  };

  const updateCapabilityMedia = (index, field, value) => {
    const newItems = capabilitiesItems.map((item, i) =>
      i === index ? {
        ...item,
        media: {
          ...item.media,
          [field]: value
        }
      } : item
    );
    dispatch(setValueCapabilities({
      value: {
        ...capabilitiesData,
        items: newItems
      }
    }));
  };

  const updateMediaType = (index, value) => {
    const newItems = capabilitiesItems.map((item, i) => {
      if (i === index) {
        return {
          ...item,
          media: {
            mediaType: value || DEFAULT_MEDIA_TYPE,
            mediaUrl: "",
            mediaFileId: ""
          }
        };
      }
      return item;
    });

    dispatch(setValueCapabilities({
      value: {
        ...capabilitiesData,
        items: newItems
      }
    }));
  };

  const validateAllCapabilities = () => ({});

  useImperativeHandle(ref, () => ({
    addGroup,
    validateAllCapabilities
  }));

  const renderCapabilityItem = (item, index) => {
    const mediaType = item.media?.mediaType || DEFAULT_MEDIA_TYPE;
    const acceptTypes = mediaType === 'video'
      ? ''
      : '.png,.jpg,.jpeg,.PNG,.JPG,.JPEG';

    return (
      <div key={index}>
        <div className="domain-group">
          <div className='package-input'>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '8px'
            }}>
              <label>capability Title:</label>
            </div>
            <CustomInput
              type="text"
              value={item.title || ''}
              onChange={(e) => updateCapabilityItem(index, 'title', e.target.value)}
              placeholder={PLACEHOLDER_CONFIG.capabilityTitle[language]}
              customStyle={{ width: '73%' }}
            />
            <button
              className="icon-btn"
              onClick={() => showDeleteGroupConfirmation(index)}
            >
              <img src={DeleteIcon} alt="Delete" className="campaign-delete-icon" />
            </button>
          </div>

          <div className='package-input'>
            <label>capability Description:</label>
            <textarea
              placeholder={PLACEHOLDER_CONFIG.capabilityDescription[language]}
              value={item.description || ''}
              onChange={(e) => updateCapabilityItem(index, 'description', e.target.value)}
              rows={3}
              style={{ width: '73%' }}
            />
          </div>

          <div className='package-input'>
            <label>Type:</label>
            <select
              value={item.media?.mediaType || DEFAULT_MEDIA_TYPE}
              onChange={(e) => updateMediaType(index, e.target.value)}
              style={{ width: '73%', height: '40px', padding: '0 12px', outline: 'none', border: '1px solid #ddd' }}
            >
              {MEDIA_TYPES.map(type => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
          </div>

          {mediaType === 'video' && (
            <div style={{ position: 'relative' }}>
              <div className='package-input'>
                <label>Url:</label>
                <input
                  type="text"
                  placeholder={PLACEHOLDER_CONFIG.mediaUrl[language]}
                  value={item.media?.mediaUrl || ''}
                  onChange={(e) => updateCapabilityMedia(index, 'mediaUrl', e.target.value)}
                  style={{ width: '73%' }}
                />
                {item.media?.mediaUrl && (
                  <button
                    className="icon-btn"
                    onClick={() => handleShowDeleteFile(index, "mediaUrl")}
                  >
                    <img src={DeleteIcon} alt="Delete" className="action-icon" />
                  </button>
                )}
              </div>
            </div>
          )}

          {mediaType === 'image' && (
            <div style={{ position: 'relative' }}>
              <div className='package-input'>
                <label>Url:</label>
                <input
                  type="text"
                  placeholder={PLACEHOLDER_CONFIG.mediaUrl[language]}
                  value={item.media?.mediaUrl || ''}
                  disabled
                  style={{ width: '73%' }}
                />
                {!item.media?.mediaUrl ? (
                  <button
                    className="upload-btn"
                    onClick={() => handleShowUploadFile(index, "mediaUrl")}
                  >
                    Upload
                  </button>
                ) : (
                  <button
                    className="icon-btn"
                    onClick={() => handleShowDeleteFile(index, "mediaUrl")}
                  >
                    <img src={DeleteIcon} alt="Delete" className="action-icon" />
                  </button>
                )}
              </div>
            </div>
          )}

          {mediaType === 'image' && item.media?.mediaUrl && (
            <div style={{ marginLeft: '190px', marginBottom: '20px' }}>
              <img
                src={`https://uat.stack-ez.com${item.media?.mediaUrl}`}
                alt="Preview"
                style={{ maxWidth: '200px', maxHeight: '200px', marginTop: '10px' }}
              />
            </div>
          )}

          <ModalFileUpload
            show={showFileUploadModal && selectedCapabilityIndex === index}
            handleCancel={handleCancelUploadFile}
            handleUpload={handleFileUpload}
            isLoading={isUploading}
            acceptTypes={acceptTypes}
            title={mediaType === 'video' ? "Upload Video" : "Upload Image"}
          />
        </div>

        {index !== capabilitiesItems.length - 1 && (
          <div
            style={{
              height: '0.5px',
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              margin: '25px -40px',
              width: 'calc(100% + 80px)',
              display: 'block',
              boxSizing: 'border-box'
            }}
          />
        )}
      </div>
    );
  };

  return (
    <div className="tab-package-container">
      <div className="domain-group">
        <div className='package-input'>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '8px'
          }}>
            <label>Title:</label>
          </div>
          <CustomInput
            type="text"
            value={capabilitiesTitle}
            onChange={(e) => updateCapabilities('title', e.target.value)}
            placeholder={PLACEHOLDER_CONFIG.title[language]}
            customStyle={{ width: '73%' }}
          />
        </div>

        <div className='package-input'>
          <label>Description:</label>
          <textarea
            placeholder={PLACEHOLDER_CONFIG.description[language]}
            value={capabilitiesDescription || ''}
            onChange={(e) => updateCapabilities('description', e.target.value)}
            rows={3}
            style={{ width: '73%' }}
          />
        </div>
      </div>

      <div
        style={{
          height: '0.5px',
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          margin: '25px -40px',
          width: 'calc(100% + 80px)',
          display: 'block',
          boxSizing: 'border-box'
        }}
      />

      {capabilitiesItems.map(renderCapabilityItem)}

      <CommonDeleteModal
        show={!isEmpty(isShowDeleteAsset)}
        onHide={() => setIsShowDeleteAsset(null)}
        onConfirm={handleDeleteFile}
      />

      <CommonDeleteModal
        show={showDeleteGroupModal}
        onHide={cancelDeleteGroup}
        onConfirm={confirmDeleteGroup}
        title="Delete Capability Group"
        instruction="Are you sure you want to delete this capability group?"
      />
    </div>
  );
});

export default Capabilities;